import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currency';
import type { BillSummaryBucket } from '../types';

const DASH = '—';

/**
 * Slim billed / paid bar for ONE contract, above the bills table.
 *
 * Scoped to a contract on purpose: every bill of a contract carries that
 * contract's currency, so once a contract is picked there is exactly one
 * currency and the totals are two plain numbers. Without a contract the figures
 * would mix currencies, so the bar stays muted and says what to do instead.
 *
 * Totals cover every bill matching the active filters, not just the page. Kept
 * to a single row so it never pushes the table below the fold.
 */

/** One inline label + value pair. */
function Figure({
  label,
  value,
  accent = false,
  isLoading = false,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  isLoading?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">
        {label}
      </span>
      {isLoading ? (
        <span className="inline-block h-4 w-24 animate-pulse rounded bg-slate-200" />
      ) : (
        <span
          className={cn(
            'text-sm font-semibold tabular-nums',
            accent ? 'text-green-700' : 'text-slate-900'
          )}
        >
          {value}
        </span>
      )}
    </div>
  );
}

/** Shell — same slim height in every state so the table never shifts. */
function Bar({
  children,
  muted = false,
}: {
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        'mb-3 flex flex-wrap items-center gap-x-6 gap-y-1.5 rounded-lg border px-4 py-2.5',
        muted ? 'border-dashed border-slate-300 bg-slate-50/60' : 'border-slate-200 bg-white'
      )}
    >
      {children}
    </div>
  );
}

export function BillsSummary({
  buckets,
  hasContract,
  isLoading = false,
}: {
  buckets: BillSummaryBucket[] | undefined;
  /** A contract is selected in the filters — without one there is no single currency. */
  hasContract: boolean;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();

  // No contract picked — the totals are not meaningful yet. Say so rather than
  // hiding the bar, so the absence is explained instead of mysterious.
  if (!hasContract) {
    return (
      <Bar muted>
        <span className="text-[0.8125rem] text-muted-foreground">
          {t('billsSummary.selectContract')}
        </span>
      </Bar>
    );
  }

  // A contract has exactly one currency, so there is exactly one bucket.
  const bucket = buckets?.[0];

  if (isLoading && !bucket) {
    return (
      <Bar>
        <Figure label={t('billsSummary.billed')} value={DASH} isLoading />
        <Figure label={t('billsSummary.paid')} value={DASH} accent isLoading />
      </Bar>
    );
  }

  // Contract selected but nothing matches (e.g. combined with a state filter).
  // The currency is unknown with no bills, so say so rather than show a fake 0.
  if (!bucket) {
    return (
      <Bar muted>
        <span className="text-[0.8125rem] text-muted-foreground">
          {t('billsSummary.noBills')}
        </span>
      </Bar>
    );
  }

  const money = (amount: number) =>
    formatCurrency(amount, { locale: 'uz-UZ', currency: bucket.currency });

  // Guard the divide: a filtered set can legitimately total zero (all-zero
  // drafts), and 0/0 must read as "—", never NaN%.
  const collectedPct = bucket.billed > 0 ? (bucket.paid / bucket.billed) * 100 : null;
  // A real but tiny share must not round down to a flat "0%" — that reads as
  // "nothing was paid" when money actually came in.
  const collected =
    collectedPct == null
      ? null
      : collectedPct > 0 && collectedPct < 1
        ? '<1'
        : String(Math.round(collectedPct));
  const outstanding = bucket.billed - bucket.paid;

  return (
    <Bar>
      <div className={cn('flex flex-wrap items-center gap-x-6 gap-y-1.5', isLoading && 'opacity-60')}>
        <Figure label={t('billsSummary.billed')} value={money(bucket.billed)} />
        <Figure
          label={t('billsSummary.paid')}
          accent
          value={
            <>
              {money(bucket.paid)}
              {collected != null && (
                <span className="ml-1.5 font-normal text-muted-foreground">
                  ({t('billsSummary.collected', { percent: collected })})
                </span>
              )}
            </>
          }
        />
        {outstanding > 0 && (
          <Figure label={t('billsSummary.outstandingLabel')} value={money(outstanding)} />
        )}
      </div>
      <span className="ml-auto text-[0.75rem] text-muted-foreground whitespace-nowrap">
        {t('billsSummary.billsCount', { count: bucket.count })}
      </span>
    </Bar>
  );
}
